import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ProductsWrapper } from "./styled";
import {
  Box,
  ButtonStyledIconLeft,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import {
  PlusIcon,
  ProductCategories,
  SearchIcon,
} from "../../../components/styled/AllImages";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import ListTable from "../../../components/common/tables/ListTable";
import { productsCategoriesColumns } from "../../../helpers/adminConstants/TableConstants";
import CreateCategoryModal from "../../../components/admin/Modals/CreateCategory/CreateCategoryModal";
import EditCategoryModal from "../../../components/admin/Modals/CreateCategory/EditCategoryModal";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import { useDebounce } from "../../../hooks/useDebounce";
import CategoriesApi from "../../../services/api/categoriesApi";
import { toast } from "react-toastify";
import { getModuleOperations } from "../../../utils";

const ProductsCategories = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();

  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [categoryId, setCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  let options = ["view", "edit", "delete"];

  const onDelete = (row) => {
    setCategoryId(row.id);
    setShowDeleteModal(true);
  };

  const handleView = (category) => {
    navigate(`/products-categories/${category.id}/products`);
  };

  const onEdit = (row) => {
    setCategoryId(row.id);
    setShowEditCategoryModal(true);
  };
  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query);
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await CategoriesApi.deleteCategory(categoryId);
      if (response.code === 200) {
        toast.success(t("category_deleted_successfully"));
        dispatchEvent(new Event("productCategoryUpdated"))
        setShowDeleteModal(false);
        setCategoryId(null);
        getCategoriesList(activePage);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  // const handleEdit = async () => {
  //   try {
  //     setLoadingEdit(true);
  //     const response = await CategoriesApi.editCategory(categoryId);
  //     if (response.code === 200) {
  //       toast.success(t("category_edited_successfully"));

  //       setCategoryId(null);
  //       getCategoriesList(activePage);
  //     }
  //     if (response.code === 100 || response.code === 204) {
  //       toast.error(response.message);
  //     }
  //     if (response.code === 400) {
  //       toast.error(response?.error);
  //     }
  //   } catch (error) {}finally {
  //     setLoadingEdit(false);
  //   }
  // };

  const getCategoriesList = async (page = 1, query = "") => {
    try {
      setLoading(true);
      const { data, code } = await CategoriesApi.categoriesList(page, query);
      if (code === 200) {
        setRowsData(data?.data);
        setTotalCount(data?.total);
        setPerPage(data?.per_page);
      } else {
        setRowsData([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategoriesList(activePage, debouncedSearchQuery);
  }, [activePage, debouncedSearchQuery, language]);

  return (
    <ProductsWrapper>
      <Box className="content-head">
        <Box className="w-fit d-flex align-items-center gap-3">
          <ProductCategories />
          <SubHeadingStyled size="16px" weight="700" color="var(--text)">
            {t("product_categories")}
          </SubHeadingStyled>
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          <FloatingInput
            name="search"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            icon={SearchIcon}
            onChange={handleSearch}
          />

          {getModuleOperations(2, 1) && (
            <ButtonStyledIconLeft
              label={t("create_category")}
              icon={PlusIcon}
              onClick={() => setShowCreateCategoryModal(true)}
            />
          )}
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            typeId={2}
            options={options}
            columns={productsCategoriesColumns}
            rows={rowsData}
            onDelete={onDelete}
            totalCount={totalCount}
            perPage={perPage}
            activePage={activePage}
            setActivePage={setActivePage}
            onEdit={onEdit}
            onOpen={handleView}
          />
        )}
      </Box>

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        title={t("category")}
        isLoading={loadingDelete}
      />
      <CreateCategoryModal
        title={t("create_new_category")}
        show={showCreateCategoryModal}
        onHide={() => setShowCreateCategoryModal(false)}
        getCategoriesList={getCategoriesList}
        activePage={activePage}
      />
      <EditCategoryModal
        title={t("edit_category")}
        show={showEditCategoryModal}
        onHide={() => setShowEditCategoryModal(false)}
        categoryData={rowsData.find(
          (row) => categoryId && row.id === categoryId
        )}
        getCategoriesList={getCategoriesList}
        activePage={activePage}
      />
    </ProductsWrapper>
  );
};

export default ProductsCategories;
